import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, switchMap, take } from 'rxjs';
import { RouterUrlsEnum } from '../enums/router-urls.enum';
// Import CustomerIdentityService from its source module, not the
// '@bodyanalytics/ui-core' barrel — this guard is itself re-exported by that
// barrel, so a barrel import would create a circular import-alias that breaks
// Jest compilation for consumers (same reason admin-role.guard imports
// AdminAuthService by path).
import { CustomerIdentityService } from '../services/auth/customer-identity.service';

/**
 * Backend contract mirror: the profile endpoint tags its "no Customer row" 404
 * with this `code` (see `CUSTOMER_NOT_FOUND_CODE` in
 * `libs/back/api/manage-customers-api/src/lib/customer-self-service-api.service.ts`).
 * The two Nx project graphs cannot import across each other, so the literal is
 * duplicated and MUST stay equal (same convention as `DEFAULT_TIMEZONE`).
 */
const CUSTOMER_NOT_FOUND_CODE = 'CUSTOMER_NOT_FOUND';

/**
 * True only when a 404 genuinely signals "this authenticated identity has no
 * Customer row" — i.e. the profile endpoint returned its tagged error body. A
 * bare/untagged 404 (e.g. a stale or misrouted deployment where the route itself
 * is missing) is NOT a reliable "unregistered" signal and must NOT be treated as
 * one, otherwise an existing customer is trapped in a register → login loop (C10).
 */
function isNoCustomerError(err: HttpErrorResponse): boolean {
  const body = err.error as { code?: unknown } | null | undefined;
  return !!body && typeof body === 'object' && body.code === CUSTOMER_NOT_FOUND_CODE;
}

/**
 * Customer-portal identity gate (#840 / T-28).
 *
 * Auth0's `AuthGuard` only proves the visitor is *authenticated*. This guard adds
 * the *identity* check the customer portal needs: it confirms the Auth0 email maps
 * to a real Customer row (via the JWT-scoped self profile endpoint) and routes
 * accordingly:
 *   - not authenticated              → kick off Auth0 login, stashing the target url
 *   - authenticated + Customer        → allow (200)
 *   - authenticated, no Customer row  → redirect to /register/profile — the
 *     create-profile step that actually creates the Customer row (#1760). This
 *     deliberately SKIPS /register/account: the caller is already authenticated,
 *     so their Auth0 account exists, and the account step's signup POST would
 *     400 with "user exists" and loop them forever (the #1760 dead-end).
 *     (ONLY when the 404 carries the CUSTOMER_NOT_FOUND code)
 *   - token without email claim       → re-login (401)
 *   - any other error (incl. an untagged/route-missing 404) → fail closed to /error
 *
 * Wire it AFTER `AuthGuard` on a route's `canActivate`; Angular evaluates
 * `canActivate` guards in order and short-circuits on the first block, so the
 * profile probe only runs once authentication has resolved.
 */
export const customerIdentityGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const identity = inject(CustomerIdentityService);

  return auth.isAuthenticated$.pipe(
    take(1),
    switchMap(isAuthenticated => {
      if (!isAuthenticated) {
        auth.loginWithRedirect({ appState: { target: state.url } });
        return of(false);
      }
      return identity.verify().pipe(
        map(() => true as boolean | UrlTree),
        catchError((err: unknown) => {
          if (err instanceof HttpErrorResponse) {
            if (err.status === 401) {
              // Token carries no usable identity → re-authenticate.
              auth.loginWithRedirect({ appState: { target: state.url } });
              return of(false);
            }
            // ONLY a 404 the endpoint explicitly tagged as "no Customer row" means
            // the user is genuinely unregistered. An untagged 404 (stale/misrouted
            // deployment) is NOT trustworthy and must fall through to the
            // fail-closed path below, not loop the user to registration (C10).
            //
            // Target: /register/profile, NOT /register/account (#1760). This branch
            // is only reachable AUTHENTICATED (the guard checked above), so the
            // Auth0 account already exists — what's missing is the Customer row,
            // which the profile step creates. Unauthenticated visitors never reach
            // here and keep the loginWithRedirect behavior.
            if (err.status === 404 && isNoCustomerError(err)) {
              return of(router.parseUrl(RouterUrlsEnum.AUTH_REGISTER_PROFILE));
            }
          }
          // Fail closed: any unexpected error — including an untagged/route-missing
          // 404 — denies entry rather than rendering a half-working member surface.
          return of(router.parseUrl('/error'));
        }),
      );
    }),
  );
};
